// @ts-nocheck
import React, { useState } from 'react';
import { userService, taskService } from '@/firebase/database';
import { useAuth } from '@/contexts/AuthContext';

const DatabaseTest: React.FC = () => {
  const { user } = useAuth();
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [userData, setUserData] = useState<any>(null);

  const addResult = (result: string) => {
    setTestResults(prev => [...prev, result]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const testUserOperations = async () => {
    if (!user) {
      addResult('❌ No user logged in - cannot test user operations');
      return;
    }

    addResult('🧪 Testing User Operations...');
    
    // Test 1: Create/Update User
    try {
      addResult('📝 Attempting to create/update user...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('User creation timeout after 10 seconds')), 10000)
      );
      
      const userCreationPromise = userService.createUser(user.id, {
        name: user.name,
        email: user.email,
        picture: user.picture,
        lastLogin: new Date().toISOString(),
        testData: 'Database test completed'
      });
      
      const userResult = await Promise.race([userCreationPromise, timeoutPromise]);
      
      addResult(`📝 User creation result: ${JSON.stringify(userResult)}`);
      
      if (userResult.success) {
        addResult('✅ User created/updated successfully');
      } else {
        addResult(`❌ User creation failed: ${userResult.error}`);
        return; // Stop if user creation fails
      }
    } catch (error) {
      addResult(`❌ User creation error: ${error.message || error}`);
      return; // Stop if there's an error
    }

    // Test 2: Get User Data
    try {
      addResult('📖 Attempting to get user data...');
      const getUserResult = await userService.getUser(user.id);
      addResult(`📖 User retrieval result: ${JSON.stringify(getUserResult)}`);
      
      if (getUserResult.success) {
        setUserData(getUserResult.data);
        addResult('✅ User data retrieved successfully');
        addResult(`📊 User data: ${JSON.stringify(getUserResult.data, null, 2)}`);
      } else {
        addResult(`❌ User retrieval failed: ${getUserResult.error}`);
        return; // Stop if user retrieval fails
      }
    } catch (error) {
      addResult(`❌ User retrieval error: ${error}`);
      return; // Stop if there's an error
    }

    // Test 3: Update User Data
    try {
      addResult('✏️ Attempting to update user data...');
      const updateResult = await userService.updateUser(user.id, {
        lastTestRun: new Date().toISOString(),
        testStatus: 'completed'
      });
      
      addResult(`✏️ User update result: ${JSON.stringify(updateResult)}`);
      
      if (updateResult.success) {
        addResult('✅ User data updated successfully');
      } else {
        addResult(`❌ User update failed: ${updateResult.error}`);
      }
    } catch (error) {
      addResult(`❌ User update error: ${error}`);
    }
    
    addResult('✅ User operations completed successfully');
  };

  const testTaskOperations = async () => {
    if (!user) {
      addResult('❌ No user logged in - cannot test task operations');
      return;
    }

    addResult('🧪 Testing Task Operations...');
    
    // Test 1: Create Multiple Tasks
    const testTasks = [
      {
        title: 'Database Test Task 1',
        description: 'Testing task creation functionality',
        completed: false,
        priority: 'high'
      },
      {
        title: 'Database Test Task 2',
        description: 'Testing task creation with different data',
        completed: true,
        priority: 'medium'
      },
      {
        title: 'Database Test Task 3',
        description: 'Testing task creation with special characters: !@#$%^&*()',
        completed: false,
        priority: 'low'
      }
    ];

    const createdTaskIds: string[] = [];

    for (const task of testTasks) {
      try {
        const taskResult = await taskService.createTask(user.id, task);
        if (taskResult.success) {
          createdTaskIds.push(taskResult.id);
          addResult(`✅ Task created: "${task.title}" (ID: ${taskResult.id})`);
        } else {
          addResult(`❌ Task creation failed for "${task.title}": ${taskResult.error}`);
        }
      } catch (error) {
        addResult(`❌ Task creation error for "${task.title}": ${error}`);
      }
    }

    // Test 2: Get All Tasks
    try {
      const getTasksResult = await taskService.getTasks(user.id);
      if (getTasksResult.success) {
        setTasks(getTasksResult.data);
        addResult(`✅ Retrieved ${getTasksResult.data.length} tasks`);
        addResult(`📊 Tasks: ${JSON.stringify(getTasksResult.data, null, 2)}`);
      } else {
        addResult(`❌ Task retrieval failed: ${getTasksResult.error}`);
      }
    } catch (error) {
      addResult(`❌ Task retrieval error: ${error}`);
    }

    // Test 3: Update First Task
    if (createdTaskIds.length > 0) {
      try {
        const updateResult = await taskService.updateTask(createdTaskIds[0], {
          title: 'Updated Database Test Task 1',
          completed: true,
          updatedAt: new Date().toISOString()
        });
        
        if (updateResult.success) {
          addResult(`✅ Task updated successfully (ID: ${createdTaskIds[0]})`);
        } else {
          addResult(`❌ Task update failed: ${updateResult.error}`);
        }
      } catch (error) {
        addResult(`❌ Task update error: ${error}`);
      }
    }

    // Test 4: Delete Second Task
    if (createdTaskIds.length > 1) {
      try {
        const deleteResult = await taskService.deleteTask(createdTaskIds[1]);
        if (deleteResult.success) {
          addResult(`✅ Task deleted successfully (ID: ${createdTaskIds[1]})`);
        } else {
          addResult(`❌ Task deletion failed: ${deleteResult.error}`);
        }
      } catch (error) {
        addResult(`❌ Task deletion error: ${error}`);
      }
    }
  };

  const testRealTimeUpdates = () => {
    if (!user) {
      addResult('❌ No user logged in - cannot test real-time updates');
      return;
    }

    addResult('🧪 Testing Real-time Updates...');
    
    try {
      const unsubscribe = taskService.subscribeToTasks(user.id, (updatedTasks) => {
        setTasks(updatedTasks);
        addResult(`🔄 Real-time update: ${updatedTasks.length} tasks received`);
      });
      
      addResult('✅ Real-time listener established');
      
      // Cleanup after 30 seconds
      setTimeout(() => {
        unsubscribe();
        addResult('🔄 Real-time listener cleaned up');
      }, 30000);
      
    } catch (error) {
      addResult(`❌ Real-time listener error: ${error}`);
    }
  };

  const runAllTests = async () => {
    setIsLoading(true);
    clearResults();
    
    addResult('🚀 Starting Comprehensive Database Test...');
    addResult(`👤 Testing with user: ${user?.name} (${user?.email})`);
    addResult('='.repeat(50));
    
    // First, test basic Firebase connection
    addResult('🔍 Testing basic Firebase connection...');
    try {
      const { db, isFirebaseConfigured } = await import('@/firebase/config');
      addResult(`🔍 Firebase configured: ${isFirebaseConfigured()}`);
      addResult(`🔍 Database instance: ${db ? 'Available' : 'Not available'}`);
      
      if (!isFirebaseConfigured() || !db) {
        addResult('❌ Firebase not properly configured - stopping tests');
        setIsLoading(false);
        return;
      }
    } catch (error) {
      addResult(`❌ Firebase import error: ${error.message || error}`);
      setIsLoading(false);
      return;
    }
    
    await testUserOperations();
    addResult('='.repeat(50));
    await testTaskOperations();
    addResult('='.repeat(50));
    testRealTimeUpdates();
    
    addResult('='.repeat(50));
    addResult('🎉 Database test completed!');
    
    setIsLoading(false);
  };

  const cleanupTestData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    addResult('🧹 Cleaning up test data...');
    
    try {
      const getTasksResult = await taskService.getTasks(user.id);
      if (getTasksResult.success) {
        const testTasks = getTasksResult.data.filter((task: any) => 
          task.title.includes('Database Test Task')
        );
        
        for (const task of testTasks) {
          await taskService.deleteTask(task.id);
          addResult(`🗑️ Deleted test task: ${task.title}`);
        }
        
        addResult(`✅ Cleaned up ${testTasks.length} test tasks`);
      }
    } catch (error) {
      addResult(`❌ Cleanup error: ${error}`);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Database Function Test</h2>
      <p className="text-gray-600 mb-4">
        This comprehensive test will verify all Firebase database operations including user management, task CRUD operations, and real-time updates.
      </p>

      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={runAllTests}
          className="btn-primary px-4 py-2"
          disabled={isLoading || !user}
        >
          {isLoading ? 'Testing...' : 'Run All Database Tests'}
        </button>
        
        <button
          onClick={cleanupTestData}
          className="btn-secondary px-4 py-2"
          disabled={isLoading || !user}
        >
          {isLoading ? 'Cleaning...' : 'Cleanup Test Data'}
        </button>
        
        <button
          onClick={clearResults}
          className="btn-secondary px-4 py-2"
        >
          Clear Results
        </button>
      </div>

      {!user && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          ⚠️ Please log in to run database tests
        </div>
      )}

      {testResults.length > 0 && (
        <div className="bg-gray-100 p-4 rounded-md mb-6">
          <h3 className="text-lg font-semibold mb-2">Test Results:</h3>
          <div className="max-h-96 overflow-y-auto">
            {testResults.map((result, index) => (
              <div key={index} className="text-sm font-mono mb-1">
                {result}
              </div>
            ))}
          </div>
        </div>
      )}

      {userData && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Current User Data:</h3>
          <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
            {JSON.stringify(userData, null, 2)}
          </pre>
        </div>
      )}

      {tasks.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Current Tasks ({tasks.length}):</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {tasks.map(task => (
              <div key={task.id} className="bg-white p-3 rounded-md shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${task.completed ? 'line-through text-gray-500' : ''}`}>
                      {task.title}
                    </span>
                    <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
                      {task.priority || 'normal'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      task.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.completed ? 'Completed' : 'Pending'}
                    </span>
                    <button
                      onClick={() => taskService.deleteTask(task.id)}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {task.description && (
                  <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTest;
